import { useState } from 'react'
import { useAccount, useWriteContract, useReadContract } from 'wagmi'
import { parseUnits } from 'viem'
import { CONTRACT_ADDRESSES, USDC_ADDRESSES } from '../config/wagmi'
import { BASE_IT_NFT_ABI, USDC_ABI } from '../config/contracts'
import { generateMetadata } from '../config/nftMetadata'

// Upload metadata to IPFS via Vercel function
const uploadMetadataToIPFS = async (metadata) => {
  try {
    console.log('📤 Uploading metadata to IPFS...')

    const uploadResponse = await fetch('/api/upload_metadata', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        metadata: metadata
      })
    })

    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json()
      throw new Error(`Server metadata upload failed: ${errorData.error || uploadResponse.statusText}`)
    }

    const result = await uploadResponse.json()
    if (!result.success) {
      throw new Error(`Server upload failed: ${result.error}`)
    }

    console.log(`✅ Metadata uploaded to IPFS: ${result.metadataIpfsHash}`)
    return result.metadataIpfsHash
  } catch (error) {
    console.error('Error uploading metadata to IPFS:', error)
    throw new Error(`Failed to upload metadata to IPFS: ${error.message}`)
  }
}

export function useMintNFT() {
  const [isMinting, setIsMinting] = useState(false)
  const [mintResult, setMintResult] = useState(null)
  const [error, setError] = useState(null)
  
  const { address, chainId, isConnected } = useAccount()
  const { writeContract, writeContractAsync } = useWriteContract()
  
  // Get contract addresses for current chain
  const contractAddress = CONTRACT_ADDRESSES[chainId]
  const usdcAddress = USDC_ADDRESSES[chainId]
  
  // Get current total supply from contract
  const { data: currentSupply } = useReadContract({
    address: contractAddress,
    abi: BASE_IT_NFT_ABI,
    functionName: 'totalSupply'
  })

  // Get contract state info using the getContractInfo function
  const { data: contractInfo } = useReadContract({
    address: contractAddress,
    abi: BASE_IT_NFT_ABI,
    functionName: 'getContractInfo'
  })
  
  // Debug logging
  console.log('🔍 Wallet Debug:', {
    chainId,
    contractAddress,
    usdcAddress,
    userAddress: address,
    isConnected
  })

  // Read mint price from contract
  const { data: mintPrice } = useReadContract({
    address: contractAddress,
    abi: BASE_IT_NFT_ABI,
    functionName: 'mintPriceUSDC',
    enabled: !!contractAddress
  })

  // Read USDC balance
  const { data: usdcBalance } = useReadContract({
    address: usdcAddress,
    abi: USDC_ABI,
    functionName: 'balanceOf',
    args: [address],
    enabled: !!address && !!usdcAddress
  })

  // Read USDC allowance
  const { data: usdcAllowance } = useReadContract({
    address: usdcAddress,
    abi: USDC_ABI,
    functionName: 'allowance',
    args: [address, contractAddress],
    enabled: !!address && !!usdcAddress && !!contractAddress
  })

  const mintNFT = async (imageUrl) => {
    if (!isConnected || !contractAddress || !usdcAddress) {
      throw new Error('Wallet not connected or contract not found')
    }

    setIsMinting(true)
    setError(null)
    setMintResult(null)

    try {
      // Calculate the next token ID from current supply
      const nextTokenId = (currentSupply || 0n) + 1n

      // Generate metadata using the actual token ID
      const metadata = generateMetadata({
        imageUrl: imageUrl,
        index: Number(nextTokenId) // Use the actual token ID that will be assigned
      })
      
      console.log('🔍 Generated metadata:', JSON.stringify(metadata, null, 2))

      // Use data URI for MetaMask compatibility
      const tokenURI = `data:application/json;base64,${btoa(JSON.stringify(metadata))}`
      
      console.log('🔍 Debug Info:', {
        tokenURI: tokenURI.substring(0, 100) + '...',
        tokenURILength: tokenURI.length,
        metadata: metadata,
        nextTokenId: Number(nextTokenId)
      })

      // Check contract state before minting
      console.log('🔍 Contract state check:')
      console.log('- Current supply:', Number(currentSupply))
      console.log('- Contract info:', contractInfo)
      console.log('- Next token ID:', Number(nextTokenId))
      console.log('- TokenURI length:', tokenURI.length)
      console.log('- TokenURI:', tokenURI)
      
      // Validate contract state (only if we have the data)
      if (contractInfo) {
        const [currentSupplyFromContract, maxSupply, mintPrice, mintingEnabled, usdcAddress] = contractInfo
        console.log('- Max supply:', Number(maxSupply))
        console.log('- Minting enabled:', mintingEnabled)
        console.log('- Mint price:', Number(mintPrice))
        console.log('- USDC address:', usdcAddress)
        
        if (!mintingEnabled) {
          throw new Error('Minting is disabled on the contract')
        }
        if (Number(currentSupplyFromContract) >= Number(maxSupply)) {
          throw new Error('Max supply reached')
        }
      } else {
        console.log('⚠️ Contract info not available, proceeding with mint...')
        console.log('🔍 Attempting to get contract info manually...')
              }

      const mintPriceAmount = mintPrice || parseUnits('0.1', 6) // 0.1 USDC
      const hasAllowance = usdcAllowance >= mintPriceAmount
      const hasBalance = usdcBalance >= mintPriceAmount

      if (!hasBalance) {
        throw new Error(`Insufficient USDC balance. You need ${Number(mintPriceAmount) / 1000000} USDC to mint.`)
      }

      // Step 1: Approve USDC if needed
      if (!hasAllowance) {
        console.log('🔄 Step 1: Approving USDC...')
        console.log('📊 Approval details:', {
          usdcAddress,
          contractAddress,
          mintPriceAmount: mintPriceAmount.toString()
        })
        
        const approvalTx = await writeContractAsync({
          address: usdcAddress,
          abi: USDC_ABI,
          functionName: 'approve',
          args: [contractAddress, mintPriceAmount]
        })
        console.log('✅ USDC Approved:', approvalTx)
      }

      // Step 2: Mint NFT
      console.log('🔄 Step 2: Minting NFT...')
      console.log('📊 Mint details:', {
        contractAddress,
        tokenURI
      })
      
      console.log('📤 Sending mint transaction...')
      console.log('🔍 Final tokenURI being sent to contract:', tokenURI)
      console.log('🔍 TokenURI length:', tokenURI.length)
      console.log('🔍 TokenURI type:', typeof tokenURI)
      
      let txHash
      try {
        txHash = await writeContractAsync({
          address: contractAddress,
          abi: BASE_IT_NFT_ABI,
          functionName: 'mint',
          args: [tokenURI],
          gas: 800000n // Increased gas limit for data URI storage
        })
        console.log('✅ Mint transaction sent:', txHash)
      } catch (mintError) {
        console.error('❌ Mint transaction failed:', mintError)
        console.error('❌ Error details:', {
          message: mintError.message,
          code: mintError.code,
          data: mintError.data
        })
        throw mintError
      }

      if (txHash) {
        console.log('🎉 NFT Minted Successfully!')
        console.log(`🔗 Transaction: https://basescan.org/tx/${txHash}`)
        console.log(`🖼️ NFT Metadata: ${tokenURI}`)
        
        setMintResult({
          transactionHash: txHash,
          tokenURI,
          success: true
        })
      } else {
        throw new Error('Transaction failed - no hash returned')
      }

    } catch (err) {
      console.error('Minting failed:', err)
      setError(err.message || 'Failed to mint NFT')
    } finally {
      setIsMinting(false)
    }
  }

  const canMint = () => {
    console.log('🔍 canMint debug:', {
      isConnected,
      mintPrice,
      usdcBalance,
      chainId,
      contractAddress,
      usdcAddress
    })
    if (!isConnected || !mintPrice || !usdcBalance) return false
    return usdcBalance >= mintPrice
  }

  return {
    mintNFT,
    isMinting,
    mintResult,
    error,
    canMint: canMint(),
    mintPrice: mintPrice ? Number(mintPrice) / 1000000 : 0.1, // Convert to USDC units
    usdcBalance: usdcBalance ? Number(usdcBalance) / 1000000 : 0,
    isConnected,
    chainId
  }
}
